import RevenueLineDetails from '../../components/RevenueLineDetails';
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import React, { useEffect, useMemo, useRef, useState } from "react"
import Feedback from "../../components/Feedback/Feedback"
import Calendar from "../../components/Calendar"
import { formatEvents, isWonOpportunity } from "../../lib/helpers"
import { API } from "../../lib/api";
import { Alert, Autocomplete, Box, Button, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { ErrorOutline } from '@mui/icons-material';
import { resolveCalendarCustomers } from '../../lib/calendarCustomers';

const Schedule = () => {
    const [feedback, setFeedback] = useState({ open: false, message: "", type: "" })
    const [lines, setLines] = useState([])
    const [customers, setCustomers] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState([])
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7))
    const selectedYear = Number(month.slice(0, 4))
    const yearOptions = [...new Set([
        ...Array.from({ length: 11 }, (_, index) => new Date().getUTCFullYear() - 5 + index),
        selectedYear,
        ...lines.filter(line => line.shipDate && !Number.isNaN(new Date(line.shipDate).getTime())).map(line => new Date(line.shipDate).getUTCFullYear()),
    ])].sort((a, b) => a - b)
    const [selectedId, setSelectedId] = useState(null)
    const [undo, setUndo] = useState(null)
    const [filters, setFilters] = useState({ customer: '', owner: '', status: '' })
    const visibleLines = useMemo(() => lines.filter(line =>
        (!filters.customer || String(line.customerId) === filters.customer) &&
        (!filters.owner || String(line.owner?._id) === filters.owner) &&
        (!filters.status || (filters.status === 'won' ? isWonOpportunity(line) : !isWonOpportunity(line)))
    ), [lines, filters])
    const optionsFor = (id, label) => [...new Map(lines.filter(line => id(line)).map(line => [String(id(line)), label(line) || 'Unnamed'])).entries()]
    const customerOptions = [...new Map([
        ...optionsFor(line => line.customerId, line => line.customerName),
        ...customers.map(customer => [String(customer._id), customer.organizationName || 'Unnamed customer']),
    ]).entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label))
    const selected = lines.find(line => String(line.lineId) === selectedId)
    const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
    const totals = visibleLines.reduce((sum, line) => {
        if (line.shipDate?.slice(0, 7) === month) {
            const amount = Number(line.amount ?? line.weightedAmount ?? 0) || 0
            sum[isWonOpportunity(line) ? 'won' : 'potential'] += amount
        }
        return sum
    }, { won: 0, potential: 0 })
    const pending = useRef(new Set())
    const events = useMemo(() => formatEvents(visibleLines), [visibleLines])
    const overdueCount = visibleLines.filter(line => line.shipDate?.slice(0, 10) < new Date().toISOString().slice(0, 10)).length

    useEffect(() => {
        let active = true
        async function getData() {
            try {
                const [response, customerRecords] = await Promise.all([
                    API.Reporting.ShippingCalendar(), API.Customers.FetchAll(),
                ])
                if (!active) return
                if (response.status !== 200) throw new Error('Failed to load')
                const resolvedLines = await resolveCalendarCustomers(response.data, API.Opportunities.FetchById)
                if (!active) return
                setLines(resolvedLines)
                setCustomers(customerRecords)
                if (resolvedLines.some(line => !line.customerId)) {
                    setFeedback({ open: true, message: 'Some revenue lines have no customer relationship available. They appear under All customers only.', type: 'warning' })
                }
            } catch (error) {
                if (active) setFeedback({ open: true, message: 'Failed to load shipping calendar.', type: "error" })
            } finally {
                if (active) setLoading(false)
            }
        }
        getData()
        return () => { active = false }
    }, [])

    const selectLine = async lineId => {
        setSelectedId(lineId)
        const line = lines.find(item => String(item.lineId) === lineId)
        if (!line || line.quoteId) return
        try {
            const opportunity = await API.Opportunities.FetchById(line.opportunityId)
            if (opportunity?.quoteId) setLines(current => current.map(item => String(item.lineId) === lineId ? {
                ...item, quoteId: opportunity.quoteId,
                quoteNumber: opportunity.quoteNumber || item.quoteNumber,
                projectNumber: opportunity.projectNumber || item.projectNumber,
            } : item))
        } catch (error) {
            setFeedback({ open: true, message: 'Unable to load the linked quote.', type: 'error' })
        }
    }

    const changeShipDate = async (lineId, opportunityId, shipDate, isUndo = false) => {
        if (pending.current.has(lineId)) return false
        const previousDate = lines.find(line => String(line.lineId) === lineId)?.shipDate || null
        pending.current.add(lineId)
        setSaving([...pending.current])
        try {
            const response = await API.Lines.UpdateSingle(lineId, opportunityId, { shipDate })
            if (response.status !== 200) throw new Error(response.data?.message || 'Unable to update ship date.')
            setLines(current => current.map(line => String(line.lineId) === lineId ? { ...line, shipDate } : line))
            if (isUndo) {
                setUndo(current => current?.lineId === lineId ? null : current)
                setFeedback({ open: true, message: 'Ship date restored.', type: 'success' })
            } else {
                setUndo({ lineId, opportunityId, previousDate })
            }
            return true
        } catch (error) {
            setFeedback({ open: true, message: error.message || 'Unable to update ship date.', type: 'error' })
            return false
        } finally {
            pending.current.delete(lineId)
            setSaving([...pending.current])
        }
    }

    return (
        <BaseWrapper content={
            <>
                <Feedback props={feedback} setFeedback={setFeedback} />
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress /></Box>
                ) : (
                    <>
                        <Box className="hive-collapsible-grid-description" sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid',
                            borderColor: 'warning.main', borderRadius: '4px', color: 'warning.dark',
                            px: 2, py: 1.25, mb: 2, width: 'fit-content', maxWidth: '100%',
                        }}>
                            <ErrorOutline color="warning" fontSize="small" />
                            <Typography variant="body2" sx={{ color: 'inherit', fontWeight: 500, lineHeight: 1.5 }}>
                                Click a revenue line for details, hover for a preview, or drag it to change its ship date. Green indicates won; an orange edge indicates overdue.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            <TextField select size="small" label="Month" value={month.slice(5, 7)} sx={{ minWidth: 160 }}
                                onChange={event => setMonth(`${selectedYear}-${event.target.value}`)}>
                                {Array.from({ length: 12 }, (_, index) => (
                                    <MenuItem key={index} value={String(index + 1).padStart(2, '0')}>
                                        {new Date(Date.UTC(2000, index, 1)).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField select size="small" label="Year" value={selectedYear} sx={{ minWidth: 110 }}
                                onChange={event => setMonth(`${event.target.value}-${month.slice(5, 7)}`)}>
                                {yearOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                            </TextField>
                            <Autocomplete
                                size="small"
                                sx={{ width: 260, maxWidth: '100%' }}
                                options={customerOptions}
                                value={customerOptions.find(option => option.id === filters.customer) || null}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(event, option) => setFilters(current => ({ ...current, customer: option?.id || '' }))}
                                renderInput={params => <TextField {...params} label="Customer" placeholder="All customers" />}
                            />
                            {[
                                ['owner', 'Opportunity owner', optionsFor(line => line.owner?._id, line => [line.owner?.firstName, line.owner?.lastName].filter(Boolean).join(' '))],
                                ['status', 'Status', [['won', 'Won'], ['open', 'Open / not won']]],
                            ].map(([key, label, options]) => <TextField key={key} select size="small" label={label} value={filters[key]} sx={{ minWidth: 190 }}
                                onChange={event => setFilters(current => ({ ...current, [key]: event.target.value }))}>
                                <MenuItem value="">All</MenuItem>
                                {options.map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
                            </TextField>)}
                            <Button onClick={() => {
                                setFilters({ customer: '', owner: '', status: '' })
                                setMonth(new Date().toISOString().slice(0, 7))
                            }}>Clear filters</Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Totals reflect the selected month and filters. {overdueCount} overdue line(s) across all dates.</Typography>
                        <Typography variant="h6" sx={{ mb: 1 }}>{new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })} revenue</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            {[['Scheduled revenue', totals.won + totals.potential], ['Won revenue', totals.won], ['Potential revenue', totals.potential]].map(([label, value]) => (
                                <Box key={label} sx={{ flex: '1 1 180px', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                    <Typography variant="h5">{money(value)}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {undo && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUndo(null)} action={
                            <Button color="inherit" disabled={saving.includes(undo.lineId)} onClick={() => changeShipDate(undo.lineId, undo.opportunityId, undo.previousDate, true)}>Undo</Button>
                        }>Ship date updated. Undo restores the previous date.</Alert>}
                        <Calendar events={events} onShipDateChange={changeShipDate} onSelectLine={selectLine} onMonthChange={setMonth} month={month} />
                    </>
                )}
                <RevenueLineDetails line={selected} onClose={() => setSelectedId(null)} />
            </>
        } />
    )
}
export default Schedule
